import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { baseStyleSheet } from '../../../styles/styles';
import * as Colors from '../../../styles/colors';
import { LoadingSpinner } from '../../LoadingSpinner';
import { asModalComponent } from '../../Modal';
import { TextInput } from '../../TextInput';
import { Button } from '../../../components-v2/Button';
import { styleSheet } from './styles';

export interface IImageInfo {
	file: File;
	previewUrl: string;
	altText: string;
}

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IModalContext<IImageInfo> {
	className?: string;
	styles?: StyleDeclarationValue[];
}

const _ImageChooser: React.FC<IProps> = ({ className, styles, parentModal }) => {
	const [state, setState] = React.useState<{
		isLoading?: boolean;
		selectedFile?: File;
		previewUrl?: string;
		altText?: string;
		error?: string;
	}>({});

	const fileInputRef = React.useRef<HTMLInputElement>(null);

	// Clean up preview URL when component unmounts
	React.useEffect(() => {
		return () => {
			if (state.previewUrl) {
				URL.revokeObjectURL(state.previewUrl);
			}
		};
	}, [state.previewUrl]);

	const onChooseFileClicked = React.useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const onFileSelected = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Validate file type
		if (!file.type.startsWith('image/')) {
			setState(prevState => ({
				...prevState,
				error: 'Please select a valid image file',
				selectedFile: undefined,
				previewUrl: undefined,
			}));
			return;
		}

		// Validate file size (same as existing image upload logic)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			setState(prevState => ({
				...prevState,
				error: 'Image file size must be less than 10MB',
				selectedFile: undefined,
				previewUrl: undefined,
			}));
			return;
		}

		// Create preview URL
		const previewUrl = URL.createObjectURL(file);

		setState(prevState => ({
			...prevState,
			selectedFile: file,
			previewUrl,
			error: undefined,
			altText: '',
		}));
	}, []);

	const onAltTextChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setState(prevState => ({
			...prevState,
			altText: e.target.value,
		}));
	}, []);

	const onCancelClicked = React.useCallback(() => {
		// Clean up preview URL
		if (state.previewUrl) {
			URL.revokeObjectURL(state.previewUrl);
		}
		parentModal?.onRequestClose(null, true);
	}, [state.previewUrl, parentModal]);

	const onInsertClicked = React.useCallback(() => {
		const { selectedFile: stateSelectedFile, previewUrl, altText } = state;

		if (stateSelectedFile && previewUrl) {
			const imageInfo: IImageInfo = {
				file: stateSelectedFile,
				previewUrl,
				altText: altText || '',
			};
			parentModal?.onRequestClose(imageInfo, false);
		}
	}, [state, parentModal]);

	const renderContent = () => {
		const { selectedFile: renderSelectedFile, previewUrl, altText, isLoading, error } = state;

		return (
			<div className={css(baseStyleSheet.verticalStack)}>
				<div>Select an image file and optionally add alt text for accessibility</div>

				<input
					ref={fileInputRef}
					type='file'
					accept='image/*'
					onChange={onFileSelected}
					className={css(styleSheet.fileInput)}
				/>

				<Button variant='primary' onClick={onChooseFileClicked}>
					Choose Image File
				</Button>

				{error && <div className={css(styleSheet.error)}>{error}</div>}

				{renderSelectedFile && previewUrl && (
					<div className={css(styleSheet.preview)}>
						<img src={previewUrl} alt='Selected image preview' className={css(styleSheet.imagePreview)} />
						<div className={css(styleSheet.altTextInput)}>
							<div className={css(styleSheet.altTextLabel)}>Alt Text</div>
							<TextInput
								inputId='image-alt-text-input'
								onChange={onAltTextChanged}
								placeholder='Alt text (optional but recommended for accessibility)'
								type='text'
								autoComplete='off'
								value={altText || ''}
								autoFocus={true}
							/>
						</div>
					</div>
				)}

				{isLoading && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
			</div>
		);
	};

	const { selectedFile } = state;

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} image-chooser ${className || ''}`}>
			<div className={css(styleSheet.header)}>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'>
						<g fill='none' fillRule='evenodd'>
							<rect width='40' height='40' fill={Colors.success} rx='6' />
							<path
								fill='#FFF'
								d='M30 12H10c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V14c0-1.1-.9-2-2-2zm0 14H10V14h20v12zm-8-9.5c0 .83-.67 1.5-1.5 1.5S19 17.33 19 16.5 19.67 15 20.5 15s1.5.67 1.5 1.5zM12 25h16l-4-5-3 4-2-1.5z'
							/>
						</g>
					</svg>
					<span>Image</span>
				</div>
			</div>
			<div className={css(styleSheet.content)}>{renderContent()}</div>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.footer)}>
				<Button variant='primary' onClick={onInsertClicked} disabled={!selectedFile}>
					Insert
				</Button>
				<Button variant='secondary' onClick={onCancelClicked}>
					Cancel
				</Button>
			</div>
		</div>
	);
};

const ImageChooserAsObserver = observer(_ImageChooser);
const ImageChooserWithContext = inject(UserSessionViewModelKey, ModalChildComponentContextKey)(ImageChooserAsObserver);
export const ImageChooser = withEventLogging(ImageChooserWithContext, 'ImageChooser');
export const ImageChooserModal = asModalComponent(ImageChooser, {
	className: 'image-chooser-modal',
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
